import { Experiment } from '@naturalcycles/shared'
import { SupportedLocale } from '@src/cnst/translations.cnst'
import { languageContext } from '@src/context/language.context'
import { useExperimentBucket } from '@src/hooks/useExperimentBucket'
import { hotjarService } from '@src/srv/hotjar.service'
import { mixpanelService } from '@src/srv/mixpanel.service'
import { useContext, useEffect, useState } from 'react'

/**
 * Fires an experiment impression to MixPanel and Hotjar when the user is in the allowed locale
 * Conveniently returns the assigned bucket for the experiment.
 *
 * @param experiment - The experiment to fire the impression for.
 * @param allowedLocales - An array of allowed locales for the experiment.
 * @returns [bucket: string | null | undefined] Bucket value follows the signature of useExperimentBucket
 */
export function useExperiment(
  experiment: Experiment,
  allowedLocales?: SupportedLocale[],
): [bucket: string | null | undefined] {
  const { bucket: experimentBucket } = useExperimentBucket(experiment)
  const [bucket, setBucket] = useState<string | null | undefined>(null)
  const { locale } = useContext(languageContext)
  const isAllowedLocale = !allowedLocales || allowedLocales.includes(locale)

  useEffect(() => {
    if (experimentBucket === null) {
      // we are still waiting for the experiment to be returned from backend, let's keep it as null.
      return
    }

    if (!isAllowedLocale) {
      // We're not in an allowed locale, we set the bucket to undefined and do not fire the impression
      setBucket(undefined)
      return
    }

    if (!experimentBucket) {
      // We're in allowed locale, but experimentBucket is falsy, we save the value but we do not fire the impression
      setBucket(experimentBucket)
      return
    }

    // We have a bucket and we are in an allowed locale, fire the impression and save the value
    setBucket(experimentBucket)
    mixpanelService.testImpression(experiment, experimentBucket)
    console.log(
      `Impression logged for experiment ${experiment} in group ${experimentBucket} with locale ${locale}`,
    )
    hotjarService.sendHotjarEvent(experiment, experimentBucket)
  }, [experimentBucket, experiment])

  return [bucket]
}
